import { collection, onSnapshot, doc, getDocs, query, where } from 'firebase/firestore';
import mixpanel from 'mixpanel-browser';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase';
import config from '../config';
import { isIOSShell } from '../components/auth/AuthUtils';

/**
 * Opens a Stripe URL (checkout or portal) respecting the config flag
 * @param {string} url - The Stripe URL to open
 */
export function openStripeUrl(url) {
  // Apple guideline 3.1.1 (US link-out): payment pages must open in the
  // system browser, never inside the WKWebView shell. The native side
  // handles 'openExternal:' by calling UIApplication.shared.open.
  if (isIOSShell()) {
    window.webkit.messageHandlers.iOSInterface.postMessage(`openExternal:${url}`);
    return;
  }

  console.log('[openStripeUrl] OPEN_STRIPE_IN_NEW_TAB flag:', config.OPEN_STRIPE_IN_NEW_TAB);

  if (config.OPEN_STRIPE_IN_NEW_TAB) {
    console.log('[openStripeUrl] Opening in NEW TAB');
    // Open in new tab/window (recommended for better compatibility)
    window.open(url, '_blank', 'noopener,noreferrer');
  } else {
    console.log('[openStripeUrl] Opening in SAME WINDOW');
    // Open in same window
    window.location.href = url;
  }
}

/**
 * Creates a Stripe checkout session for subscription signup.
 *
 * Goes through the trusted createCheckoutSession callable — never a direct
 * Firestore write — so trial eligibility is decided server-side (14-day trial
 * only when the user has no subscription history) and can't be spoofed. The
 * checkout_sessions rule is read-only for clients.
 *
 * @param {string} userId - The Firebase Auth user ID
 * @param {string} priceId - The Stripe price ID for the subscription
 * @param {Object} [options] - Optional checkout redirect overrides
 * @param {string} [options.successUrl] - URL to return to after completed checkout
 * @param {string} [options.cancelUrl] - URL to return to after canceled checkout
 * @returns {Promise<string>} - The checkout session URL
 */
export async function createCheckoutSession(userId, priceId, options = {}) {
  try {
    // In the iOS shell, checkout completes in Safari where the user has no
    // web session, so /profile (auth-required) would bounce to /login. Land
    // on the public /subscription-complete page instead; membership state
    // syncs in the app via the Firestore subscription listener + foreground
    // token refresh (AuthContext).
    const successUrl = isIOSShell()
      ? `${window.location.origin}/subscription-complete`
      : options.successUrl || `${window.location.origin}/profile?success=true`;
    const cancelUrl = options.cancelUrl || `${window.location.origin}/subscribe?canceled=true`;

    const functions = getFunctions();
    const createSession = httpsCallable(functions, 'createCheckoutSession');
    const { data } = await createSession({ priceId, successUrl, cancelUrl });

    if (data?.url) {
      try {
        mixpanel.track('subscription_checkout_session_created', { price_id: priceId });
      } catch (_) {}
      return data.url;
    }

    if (data?.sessionId) {
      // Slow Stripe-extension run: the callable returned before the URL was
      // ready. The session doc stays owner-readable, so finish via listener.
      return waitForCheckoutUrl(userId, data.sessionId, priceId);
    }

    throw new Error('Checkout session creation failed');
  } catch (error) {
    console.error('Error creating checkout session:', error);
    try { mixpanel.track('subscription_checkout_session_exception', { message: String(error?.message || error) }); } catch (_) {}
    throw error;
  }
}

function waitForCheckoutUrl(userId, sessionId, priceId) {
  const sessionRef = doc(db, 'customers', userId, 'checkout_sessions', sessionId);
  return new Promise((resolve, reject) => {
    const unsubscribe = onSnapshot(sessionRef, (snap) => {
      const data = snap.data();
      if (data?.url) {
        unsubscribe();
        try {
          mixpanel.track('subscription_checkout_session_created', { price_id: priceId });
        } catch (_) {}
        resolve(data.url);
      } else if (data?.error) {
        unsubscribe();
        try {
          mixpanel.track('subscription_checkout_session_error', { error: data.error.message });
        } catch (_) {}
        reject(new Error(data.error.message));
      }
    });

    // Timeout after 10 seconds to prevent hanging
    setTimeout(() => {
      unsubscribe();
      try {
        mixpanel.track('subscription_checkout_session_timeout');
      } catch (_) {}
      reject(new Error('Checkout session creation timed out'));
    }, 10000);
  });
}

/**
 * Creates a Stripe customer portal session for subscription management
 * @param {string} userId - The Firebase Auth user ID (not needed for httpsCallable)
 * @returns {Promise<string>} - The customer portal URL
 */
export async function createPortalSession(userId) {
  try {
    const functions = getFunctions();
    const createPortalLink = httpsCallable(functions, 'ext-firestore-stripe-payments-createPortalLink');

    const { data } = await createPortalLink({
      returnUrl: `${window.location.origin}/profile`,
      locale: "auto", // Optional, defaults to "auto"
    });

    try { mixpanel.track('subscription_portal_session_created'); } catch (_) {}
    return data.url;
  } catch (error) {
    console.error('Error creating portal session:', error);
    try { mixpanel.track('subscription_portal_session_exception', { message: String(error?.message || error) }); } catch (_) {}
    throw error;
  }
}

/**
 * Helper function to get subscription status for a user
 * @param {string} userId - The Firebase Auth user ID
 * @returns {Promise<Object|null>} - The subscription data or null if none exists
 */
export async function getActiveSubscription(userId) {
  try {
    const subscriptionsRef = collection(db, 'customers', userId, 'subscriptions');
    const snapshot = await getDocs(query(subscriptionsRef, where('status', 'in', ['trialing', 'active'])));
    
    if (!snapshot.empty) {
      return snapshot.docs[0].data();
    }
    return null;
  } catch (error) {
    console.error('Error fetching subscription:', error);
    return null;
  }
} 